import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ThermalPrintTargetService } from 'src/app/computer/thermal-print-target.service';
import { MoneyDayService } from 'src/app/money-flow/money-day.service';
import { UserService } from 'src/app/services/user.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { resolveStableRequestId, StableRequestIdState } from 'src/app/shared/utils/request-id.util';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { MoneyTypeEnum } from 'src/enums';
import { CurrentMoneyDaySummary, User } from 'src/typings';
import { CustomerAccountService, CustomerAccountSummary, CustomerAccountOrganization, CustomerAccountPreview } from '../../customer-account/customer-account.service';
import { createRequestId } from '../../shared/utils/request-id.util';
import { accountBlockedMessage, accountError, accountMoney } from '../../customer-account/customer-account.utils';
import { OrderService } from '../order.service';
import { TerminalPaymentWaitDialogComponent } from 'src/app/terminal-payment/terminal-payment-wait-dialog.component';
import { TerminalProfileSelectDialogComponent } from 'src/app/terminal-payment/terminal-profile-select-dialog.component';
import {
  TerminalPaymentAttempt,
  TerminalPaymentService,
  TerminalProfile
} from 'src/app/terminal-payment/terminal-payment.service';

export interface OrderPaymentDialogData {
  orderId: number;
  orderLabel: string;
  clientLabel: string;
  paymentDue: number;
  invoiceCount: number;
}

export interface OrderPaymentDialogResult {
  accountOperationId?: number;
  createdPaymentId: number;
  invoiceId: number;
  invoiceCreated: boolean;
  createdPaymentIds: number[];
  invoiceIds: number[];
}

@Component({
  selector: 'app-order-payment-dialog',
  templateUrl: './order-payment-dialog.component.html',
  styleUrls: ['./order-payment-dialog.component.scss']
})
export class OrderPaymentDialogComponent implements OnInit, OnDestroy {
  paymentSource: 'NEW' | 'WALLET' | 'MIXED' = 'NEW';
  accountOrganizations: CustomerAccountOrganization[] = [];
  accountOrgId = 0;
  accountClientId = 0;
  accountSummary?: CustomerAccountSummary;
  accountLoading = false;
  accountError = '';
  accountPreview?: CustomerAccountPreview;
  accountUncertain = false;
  walletAmount = new UntypedFormControl(null, [Validators.min(0.01)]);
  private accountRequest?: { request_id: string; token: string; version: number };
  private accountRevision = 0;
  saving = false;

  moneyTypes = Object.values(MoneyTypeEnum);

  currentUser?: User;

  cashContext?: CurrentMoneyDaySummary;

  cashContextLoading = false;

  cashContextError = '';

  terminalProfiles: TerminalProfile[] = [];

  terminalProfilesLoading = false;

  terminalProfilesError = '';

  private cashPaymentRequestState?: StableRequestIdState;

  private terminalPaymentRequestState?: StableRequestIdState;

  private userSub?: Subscription;

  private paymentTypeSub?: Subscription;

  form: UntypedFormGroup = this.fb.group({
    value: new UntypedFormControl(null, [Validators.required, Validators.min(0.01)]),
    date: new UntypedFormControl(new Date(), [Validators.required]),
    payment_type: new UntypedFormControl(MoneyTypeEnum.nal, [Validators.required])
  });

  constructor(
    private fb: UntypedFormBuilder,
    private orderService: OrderService,
    private customerAccounts: CustomerAccountService,
    private notificationService: NotificationService,
    public supportService: SupportService,
    private userService: UserService,
    private moneyDayService: MoneyDayService,
    private terminalPaymentService: TerminalPaymentService,
    private thermalPrintTargetService: ThermalPrintTargetService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<OrderPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderPaymentDialogData
  ) {
    this.valueControl.setValue(this.data.paymentDue > 0 ? this.data.paymentDue : null);
  }

  ngOnInit(): void {
    this.userSub = this.userService.user.subscribe((user) => {
      this.currentUser = user;
      this.loadCashContext();
      this.loadTerminalProfiles();
      if (!this.accountClientId && !this.accountLoading) void this.loadAccountContext();
    });
    this.paymentTypeSub = this.paymentTypeControl.valueChanges.subscribe(() => {
      this.loadCashContext();
      this.invalidateAccountPreview();
    });
    this.walletAmount.valueChanges.subscribe(() => this.invalidateAccountPreview());
    this.form.valueChanges.subscribe(() => this.invalidateAccountPreview());
  }

  ngOnDestroy(): void {
    this.accountRevision++;
    this.userSub?.unsubscribe();
    this.paymentTypeSub?.unsubscribe();
  }

  get valueControl(): UntypedFormControl {
    return this.form.controls.value as UntypedFormControl;
  }

  get dateControl(): UntypedFormControl {
    return this.form.controls.date as UntypedFormControl;
  }

  get paymentTypeControl(): UntypedFormControl {
    return this.form.controls.payment_type as UntypedFormControl;
  }

  get isCashPayment() {
    return this.paymentTypeControl.value === MoneyTypeEnum.nal;
  }

  get isTerminalPayment() {
    return this.paymentTypeControl.value === MoneyTypeEnum.terminal;
  }

  get isShiftPayment() {
    return this.paymentTypeControl.value !== MoneyTypeEnum.bn;
  }

  get shiftContextLabel() {
    if (this.cashContext?.day_id) {
      return `Смена №${this.cashContext.day_id} · ${
        this.cashContext.warehouse_name || 'без склада'
      }`;
    }
    return `Смена откроется на складе ${
      this.currentUser?.defaultWarehouse?.description || 'по умолчанию (не назначен)'
    }`;
  }

  get usesIntegratedTerminalFlow() {
    return this.isTerminalPayment && this.terminalProfiles.length > 0;
  }

  get cashPaymentBlocked() {
    return Boolean(
      this.isCashPayment &&
        (this.cashContextLoading ||
          this.cashContextError ||
          (!this.cashContext?.day_id && !this.currentUser?.defaultWarehouse?.id) ||
          !this.cashContext?.cashbox_id)
    );
  }

  async save() {
    if (this.saving) {
      return;
    }
    if (this.paymentSource !== 'NEW') {
      await this.saveFromAccount();
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.warning('Заполните обязательные поля');
      return;
    }
    if (this.cashPaymentBlocked) {
      this.notificationService.warning(
        this.cashContextError || 'Для наличной оплаты не определены касса, склад или смена'
      );
      return;
    }
    if (this.isTerminalPayment && this.terminalProfilesError) {
      this.notificationService.warning(this.terminalProfilesError);
      return;
    }

    if (this.usesIntegratedTerminalFlow) {
      await this.createTrackedTerminalPayment();
      return;
    }

    this.saving = true;

    try {
      const paymentPayload = {
        value: Number(this.valueControl.value),
        payment_type: this.paymentTypeControl.value,
        date: this.dateControl.value
      };
      const requestId =
        paymentPayload.payment_type === MoneyTypeEnum.nal
          ? this.resolveCashPaymentRequestId(paymentPayload)
          : undefined;
      const result = await firstValueFrom(
        this.orderService.createPaymentFlow(this.data.orderId, {
          ...paymentPayload,
          request_id: requestId
        })
      );
      const createdPayments = (result.created_documents || []).filter(
        (doc) => doc.type === 'payment'
      );
      const createdInvoices = (result.created_documents || []).filter(
        (doc) => doc.type === 'invoice'
      );

      if (!createdPayments.length) {
        this.notificationService.warning('Не удалось создать оплату по заказу');
        return;
      }

      this.cashPaymentRequestState = undefined;

      if (createdInvoices.length) {
        this.notificationService.notice(
          `Счет ${createdInvoices.map((doc) => `№${doc.id}`).join(', ')} создан автоматически`
        );
      }

      this.notificationService.notice(
        createdPayments.length === 1
          ? `Оплата №${createdPayments[0].id} создана`
          : `Созданы оплаты: ${createdPayments.map((payment) => `№${payment.id}`).join(', ')}`
      );
      const dialogResult: OrderPaymentDialogResult = {
        createdPaymentId: createdPayments[0].id,
        invoiceId: createdInvoices[0]?.id || 0,
        invoiceCreated: createdInvoices.length > 0,
        createdPaymentIds: createdPayments.map((payment) => payment.id),
        invoiceIds: createdInvoices.map((invoice) => invoice.id)
      };
      this.dialogRef.close(dialogResult);
    } catch (err) {
      console.error(err);
      this.notificationService.warning(extractApiErrorMessage(err, 'Ошибка создания оплаты'));
    } finally {
      this.saving = false;
    }
  }

  get accountAmount() { return this.paymentSource === 'WALLET' ? accountMoney(this.valueControl.value) : accountMoney(this.walletAmount.value); }
  get accountExtra() { return accountMoney(Math.max(Number(this.valueControl.value || 0) - this.accountAmount, 0)); }
  get accountConfirmLabel() {
    if(this.accountUncertain)return 'Проверить оплату';
    if(this.paymentSource==='NEW')return 'Создать оплату';
    return this.paymentSource==='MIXED'?`Зачесть ${this.accountAmount.toFixed(2)} и принять ${this.accountExtra.toFixed(2)}`:`Зачесть ${this.accountAmount.toFixed(2)} грн`;
  }
  get accountBlockedReason() {
    const summary = this.accountSummary;
    if (this.accountError && !summary) return this.accountError;
    if (this.accountLoading) return 'Загрузка баланса…';
    if (!summary) return 'Выберите организацию и дождитесь расчёта баланса';
    if (!summary.enabled || !summary.reconciled) return accountBlockedMessage(summary.blocked_reason) || 'Операции по балансу этой организации ещё не включены или остатки не сверены';
    if (this.accountAmount <= 0) return 'Укажите сумму с баланса';
    if (this.accountAmount > Number(summary.available_credit || 0)) return 'На балансе недостаточно доступных средств';
    if (this.accountAmount > Number(this.valueControl.value || 0)) return 'Сумма с баланса превышает сумму оплаты';
    if (this.paymentSource === 'MIXED' && !this.isCashPayment) return 'Доплата с баланса сейчас поддерживает наличные. Безналичное пополнение подтвердите отдельно, затем зачтите деньги в заказ.';
    if (this.paymentSource === 'MIXED' && this.cashPaymentBlocked) return this.cashContextError || 'Дождитесь загрузки текущей кассы и смены';
    if (this.paymentSource === 'MIXED' && this.accountExtra <= 0) return 'Для полной оплаты балансом выберите «С баланса»';
    return '';
  }
  selectPaymentSource(source: 'NEW' | 'WALLET' | 'MIXED') {
    if (this.saving || this.accountUncertain) return;
    this.paymentSource = source; this.invalidateAccountPreview();
  }
  invalidateAccountPreview() {
    if (!this.accountUncertain) { this.accountPreview = undefined; this.accountRequest = undefined; }
  }
  async loadAccountContext() {
    this.accountLoading = true;
    try {
      const [order, orgs] = await Promise.all([
        firstValueFrom(this.orderService.getOne(this.data.orderId)), firstValueFrom(this.customerAccounts.organizations())
      ]);
      this.accountClientId = Number(order.client?.id || 0);
      this.accountOrganizations = orgs.data || [];
      this.accountOrgId = this.accountOrganizations.some(org => org.id === this.currentUser?.defaultOrg?.id)
        ? Number(this.currentUser?.defaultOrg?.id) : this.accountOrganizations[0]?.id || 0;
      if (!this.accountClientId) { this.accountError = 'Для использования баланса сначала назначьте клиента заказу'; return; }
      await this.loadAccountSummary();
    } catch (error) { this.accountError = accountError(error, 'Не удалось загрузить баланс клиента'); }
    finally { this.accountLoading = false; }
  }
  async loadAccountSummary() {
    const revision = ++this.accountRevision;
    this.accountSummary = undefined; this.accountError = ''; this.accountLoading = true; this.invalidateAccountPreview();
    if (!this.accountClientId || !this.accountOrgId) { this.accountLoading = false; return; }
    try {
      const summary = await firstValueFrom(this.customerAccounts.summary(this.accountClientId, this.accountOrgId));
      if (revision === this.accountRevision) this.accountSummary = summary;
    } catch (error) { if (revision === this.accountRevision) this.accountError = accountError(error, 'Не удалось загрузить баланс клиента'); }
    finally { if (revision === this.accountRevision) this.accountLoading = false; }
  }
  async calculateAccountPayment() {
    if (this.accountBlockedReason || this.saving || this.accountUncertain || this.form.invalid) return;
    this.saving = true; this.accountError = ''; this.accountPreview = undefined;
    try {
      this.accountPreview = await firstValueFrom(this.customerAccounts.preview({
        kind: 'ALLOCATE', client_id: this.accountClientId, org_id: this.accountOrgId,
        order_id: this.data.orderId, amount: this.accountAmount,
        ...(this.paymentSource === 'MIXED' ? { cash_topup: { amount: this.accountExtra, cashbox_id: Number(this.cashContext?.cashbox_id) || undefined } } : {})
      }));
    } catch (error) { this.accountError = accountError(error, 'Не удалось рассчитать оплату с баланса'); }
    finally { this.saving = false; }
  }
  private async saveFromAccount() {
    if (!this.accountPreview?.allowed || (this.accountBlockedReason && !this.accountUncertain)) return;
    this.accountRequest = this.accountRequest || { request_id: createRequestId(), token: this.accountPreview.token, version: this.accountPreview.version };
    this.saving = true; this.dialogRef.disableClose = true;
    try {
      let result:any;
      if(this.accountUncertain){try{result=await firstValueFrom(this.customerAccounts.result(this.accountRequest.request_id));}
        catch(error){if((error as any)?.status!==404)throw error;}}
      if(!result)result=await firstValueFrom(this.customerAccounts.execute(this.accountRequest));
      this.accountUncertain = false;
      this.notificationService.notice(result.operation.status === 'SUCCESS' ? 'Оплата с баланса проведена' : 'Оплата принята и ожидает выполнения');
      this.dialogRef.close({ accountOperationId: result.operation.id, createdPaymentId: 0, invoiceId: 0, invoiceCreated: false, createdPaymentIds: [], invoiceIds: [] } as OrderPaymentDialogResult);
    } catch (error: any) {
      this.accountUncertain = !error?.status || error.status >= 500;
      this.accountError = this.accountUncertain ? 'Подтверждение не получено. Проверьте эту же оплату безопасным повтором.' : accountError(error, 'Оплата не выполнена. Пересчитайте сумму.');
      if (!this.accountUncertain) { this.accountPreview = undefined; this.accountRequest = undefined; }
    } finally { this.saving = false; this.dialogRef.disableClose = this.accountUncertain; }
  }

  private async createTrackedTerminalPayment() {
    const profile = await this.resolveTerminalProfile();
    if (!profile) return;
    const agentName =
      this.thermalPrintTargetService.getDefaultAgentName(this.currentUser) ||
      (await this.thermalPrintTargetService.chooseTerminalAgent(this.currentUser));
    if (!agentName) return;

    const amount = Number(this.valueControl.value);
    this.terminalPaymentRequestState = resolveStableRequestId(
      this.terminalPaymentRequestState,
      JSON.stringify({
        order_id: this.data.orderId,
        amount,
        warehouse_id: Number(this.currentUser?.defaultWarehouse?.id || 0),
        agentName,
        terminal_profile_id: profile.id,
        provider_batch_id: profile.currentProviderBatchId || null
      })
    );
    this.saving = true;
    try {
      const attempt = await firstValueFrom(
        this.terminalPaymentService.startOrderPayment(this.data.orderId, {
          request_id: this.terminalPaymentRequestState.requestId,
          amount,
          agentName,
          warehouseId: Number(this.currentUser?.defaultWarehouse?.id || 0) || undefined,
          terminal_profile_id: profile.id,
          provider_batch_id: profile.currentProviderBatchId || undefined
        })
      );
      let result = (await firstValueFrom(
        this.dialog
          .open(TerminalPaymentWaitDialogComponent, {
            width: '460px',
            maxWidth: '95vw',
            autoFocus: false,
            disableClose: true,
            data: {
              attemptId: attempt.id,
              title: `Оплата заказа №${this.data.orderId} через терминал`
            }
          })
          .afterClosed()
      )) as TerminalPaymentAttempt | undefined;
      let bookingStatus = result?.booking_status || result?.bookingStatus;
      if (result?.status === 'APPROVED' && bookingStatus === 'FAILED') {
        result = await firstValueFrom(this.terminalPaymentService.retryBooking(result.id));
        bookingStatus = result?.booking_status || result?.bookingStatus;
      }
      const paymentIds = (result?.paymentIds || []).map(Number).filter(Boolean);
      if (result?.paymentId && !paymentIds.includes(Number(result.paymentId))) {
        paymentIds.unshift(Number(result.paymentId));
      }
      if (result?.status === 'APPROVED' && bookingStatus === 'BOOKED' && paymentIds.length) {
        this.terminalPaymentRequestState = undefined;
        this.notificationService.notice(
          paymentIds.length === 1
            ? `Терминальная оплата №${paymentIds[0]} создана`
            : `Созданы терминальные оплаты: ${paymentIds.map((id) => `№${id}`).join(', ')}`
        );
        this.dialogRef.close({
          createdPaymentId: paymentIds[0],
          invoiceId: Number(result.invoiceId || 0),
          invoiceCreated: false,
          createdPaymentIds: paymentIds,
          invoiceIds: result.invoiceId ? [Number(result.invoiceId)] : []
        } as OrderPaymentDialogResult);
        return;
      }
      if (result?.isFinal && result?.status !== 'APPROVED') {
        this.terminalPaymentRequestState = undefined;
      }
      this.notificationService.warning(
        result?.booking_error ||
          result?.bookingError ||
          result?.responseMessage ||
          result?.error ||
          'Терминальная оплата не была записана в CRM'
      );
    } catch (err) {
      console.error(err);
      this.notificationService.warning(extractApiErrorMessage(err, 'Ошибка терминальной оплаты'));
    } finally {
      this.saving = false;
    }
  }

  private async resolveTerminalProfile(): Promise<TerminalProfile | null> {
    if (this.terminalProfiles.length === 1) return this.terminalProfiles[0];
    return (await firstValueFrom(
      this.dialog
        .open(TerminalProfileSelectDialogComponent, {
          width: '500px',
          maxWidth: '95vw',
          autoFocus: false,
          data: { profiles: this.terminalProfiles }
        })
        .afterClosed()
    )) as TerminalProfile | null;
  }

  private loadTerminalProfiles() {
    const warehouseId = Number(this.currentUser?.defaultWarehouse?.id || 0);
    if (!warehouseId) {
      this.terminalProfiles = [];
      this.terminalProfilesLoading = false;
      this.terminalProfilesError = '';
      return;
    }
    this.terminalProfilesLoading = true;
    this.terminalProfilesError = '';
    this.terminalPaymentService.getProfiles(warehouseId).subscribe(
      (profiles) => {
        this.terminalProfiles = profiles;
        this.terminalProfilesLoading = false;
      },
      () => {
        this.terminalProfiles = [];
        this.terminalProfilesLoading = false;
        this.terminalProfilesError =
          'Не удалось загрузить реестр POS-терминалов. Оплата терминалом заблокирована; обратитесь к администратору.';
      }
    );
  }

  private resolveCashPaymentRequestId(payload: {
    value: number;
    payment_type: MoneyTypeEnum;
    date: Date;
  }) {
    const fingerprint = JSON.stringify({
      order_id: this.data.orderId,
      ...payload,
      date:
        payload.date instanceof Date && !Number.isNaN(payload.date.getTime())
          ? payload.date.toISOString()
          : payload.date
    });
    this.cashPaymentRequestState = resolveStableRequestId(
      this.cashPaymentRequestState,
      fingerprint
    );
    return this.cashPaymentRequestState.requestId;
  }

  private loadCashContext() {
    if (!this.isShiftPayment || !this.currentUser?.id) {
      this.cashContext = undefined;
      this.cashContextLoading = false;
      this.cashContextError = '';
      return;
    }

    this.cashContextLoading = true;
    this.cashContextError = '';
    this.moneyDayService
      .resolveCurrentMoneyDay(this.currentUser.id, this.currentUser.defaultWarehouse.id)
      .subscribe(
        (summary) => {
          this.cashContext = summary;
          this.cashContextLoading = false;
        },
        () => {
          this.cashContext = undefined;
          this.cashContextLoading = false;
          this.cashContextError = this.isCashPayment
            ? 'Не удалось загрузить остаток кассы'
            : 'Не удалось загрузить текущую смену';
        }
      );
  }
}
